import { authService, type SessionCheck } from '@/services/authService';

/**
 * Recovery from an UNREACHABLE session (#433): the server did not answer the
 * session check, the tokens are intact, and the guards show "Can't reach the
 * server". These helpers keep re-checking behind that screen so the app picks
 * itself up when the server comes back — no click needed — and keep the
 * Try again button to a single, bounded check.
 */

/** Background re-check interval while the "can't reach the server" screen is up. */
export const RECOVERY_INTERVAL_MS = 20_000;

/** Wall-clock budget for one re-check (Try again, or a background one). */
export const RECHECK_BUDGET_MS = 10_000;

/**
 * A single-flight re-check: runs ONE bounded ``checkSession``; if the server
 * answered (valid session, or a rejection), hands the result to ``apply`` and
 * resolves ``true``. Still unreachable → ``false``, nothing applied. Concurrent
 * calls (the interval firing while the user clicks) share one check.
 */
export function createSessionRecheck(
  apply: (session: SessionCheck) => void | Promise<void>,
  options: {
    check?: () => Promise<SessionCheck>;
    onError?: (error: unknown) => void;
  } = {},
): () => Promise<boolean> {
  const check = options.check ?? (() => authService.checkSessionWithin(RECHECK_BUDGET_MS));
  let inFlight: Promise<boolean> | null = null;
  return () => {
    if (inFlight) return inFlight;
    inFlight = (async () => {
      try {
        const session = await check();
        if (session.unreachable) return false;
        await apply(session);
        return true;
      } catch (error) {
        options.onError?.(error);
        return false;
      } finally {
        inFlight = null;
      }
    })();
    return inFlight;
  };
}

/**
 * Re-check when the browser comes back online, when the tab becomes visible
 * again, and every ``intervalMs``. Returns the cleanup.
 */
export function watchForRecovery(
  recheck: () => unknown,
  {
    intervalMs = RECOVERY_INTERVAL_MS,
    win = window,
    doc = document,
  }: { intervalMs?: number; win?: Pick<Window, 'addEventListener' | 'removeEventListener'>; doc?: Pick<Document, 'addEventListener' | 'removeEventListener' | 'visibilityState'> } = {},
): () => void {
  const onOnline = () => { void recheck(); };
  const onVisibility = () => {
    if (doc.visibilityState === 'visible') void recheck();
  };
  win.addEventListener('online', onOnline);
  doc.addEventListener('visibilitychange', onVisibility);
  const timer = setInterval(() => { void recheck(); }, intervalMs);
  return () => {
    win.removeEventListener('online', onOnline);
    doc.removeEventListener('visibilitychange', onVisibility);
    clearInterval(timer);
  };
}
